import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { AppDispatch } from "../../../store";
import {
  BasketItem,
  applyPromoCode,
  resetCalculatedPrices,
  resetPromo,
  setPointsEnabled,
} from "../../../store/slices/basketSlice";
import {
  getBasketItemUnitPrice,
  selectBasketCalculation,
  selectTotalPrice,
} from "../../../store/selectors/basket";
import { userInfo } from "../../../store/selectors/user";
import { impactOccuer } from "../../../utils/hapticEvents";
import { useCalculateCart } from "./calculateCart";

const readStoredNumber = (key: string) => {
  const value = localStorage.getItem(key) ?? sessionStorage.getItem(key);
  const parsed = Number(value);

  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

type Address = {
  title: string;
  location: { lat: number; lon: number };
};

type Params = {
  availableBasketItems: BasketItem[];
  userAddress?: Address | null;
  deliveryPrice: number;
  branchId: number;
  isPickupMode: boolean;
};

/**
 * Расчёт заказа: промокод, списание баллов и итоговые строки. Блок с ними
 * стоит на чекауте, а корзина берёт отсюда только итоговую сумму для кнопки;
 * вся логика здесь, экраны только рисуют.
 *
 * Баллы списываются тумблером и только целиком: сумму считает Mindbox и
 * отдаёт в `calculate-cart` полем discountAmountForCurrentOrder (лимит от
 * чека и остаток баланса уже учтены). Никаких «20% от суммы» на фронте —
 * правила Mindbox меняются без релиза. Выбрать меньшую сумму нельзя.
 */
export const useOrderCalculation = ({
  availableBasketItems,
  userAddress,
  deliveryPrice,
  branchId,
  isPickupMode,
}: Params) => {
  const dispatch = useDispatch<AppDispatch>();
  const user = useSelector(userInfo);
  const totalPrice = useSelector(selectTotalPrice);
  const { calculateCart } = useCalculateCart();
  const {
    isCalculatingCart,
    calculatedSubtotalPrice,
    calculatedDeliveryPrice,
    calculatedTotalPrice,
    appliedPromotions,
    earnedAmount,
    availableAmountForCurrentOrder,
    pointsEnabled,
    bonusPaymentPercent,
    selectedBonusPay,
    promoValidationStatus,
    promoCode,
    activePromoCode,
  } = useSelector(selectBasketCalculation);

  // Применённый код живёт в сторе (basketSlice.promoCode) и уходит в каждый
  // пересчёт; здесь — только текст в поле. При возврате на чекаут поле
  // заполняется применённым кодом, а пересчёт проверяет его заново.
  const [promoInput, setPromoInput] = useState(promoCode);
  useEffect(() => {
    // Код применили с экрана «Промокоды» или он сменился — показываем его.
    // Сброс (пустой код) поле не трогает: там может быть недописанный текст.
    if (promoCode) setPromoInput(promoCode);
  }, [promoCode]);
  // «Применить» не шлёт свой запрос, а помечает следующий пересчёт как
  // проверку кода. Раньше проверка и автопересчёт шли параллельно через один
  // AbortController и отменяли друг друга: ответ «без промокода» гасил
  // подсветку, следующий зажигал — поле и тумблер мигали.
  const validatePromoNext = useRef(false);
  // Повторное «Применить» того же кода тоже должно пересчитать корзину.
  const [promoAttempt, setPromoAttempt] = useState(0);

  // Списываемые баллы — из стора: при включённом тумблере это весь
  // discountAmountForCurrentOrder последнего расчёта.
  // Промокод и баллы вместе не применяются: с кодом в расчёте баллов нет.
  const hasPromoInCalculation = Boolean(promoCode);
  const redeemedPoints =
    pointsEnabled && !hasPromoInCalculation ? selectedBonusPay : 0;
  // Пересчёт зависит только от выбора пользователя (тумблер включён и
  // промокода нет), а не от суммы баллов: сумму присылает сам calculate-cart,
  // и если пересчитывать на каждое её изменение, ответы гоняют друг друга по
  // кругу и экран дёргается.
  const isRedeemingPoints = pointsEnabled && !hasPromoInCalculation;
  const availableToRedeem = availableAmountForCurrentOrder || 0;

  const buildCalculateCartBody = (code: string, bonusPay: number) => {
    const currentBranchId =
      readStoredNumber("branchId_key") ??
      branchId ??
      readStoredNumber("branch") ??
      0;
    const currentDeliveryPrice = deliveryPrice || 0;
    const trimmedCode = code.trim();

    return {
      payment_id: "58",
      address: {
        title: userAddress?.title || "",
        geo: userAddress
          ? `${userAddress.location.lon},${userAddress.location.lat}`
          : "",
      },
      client_id: user._id || localStorage.getItem("user_id") || "0",
      branch_id: currentBranchId,
      positions: availableBasketItems.map((item) => ({
        id: item.id,
        type: "Org",
        price: String(getBasketItemUnitPrice(item)),
        title: item.title,
        amount: item.quantity,
        modifiers: item.selectedModifiers?.map((modifier) => ({
          modifier_group_id: modifier.groupId,
          modifier_item_id:
            modifier.itemSku || modifier.item?.sku || modifier.itemId,
          quantity: modifier.quantity,
          price: modifier.price,
        })),
      })),
      comment: "",
      dop_nomer: "",
      card_id: null,
      deliveryOn: null,
      deliveryCost: currentDeliveryPrice,
      totalPrice: totalPrice + currentDeliveryPrice,
      bonusPay: !trimmedCode ? bonusPay : 0,
      promoCode: trimmedCode,
      phone_number_alt: user.phone || "+998901234567",
    };
  };

  useEffect(() => {
    // Гостю бэкенд корзину не считает.
    if (!user.isAuth) return;

    dispatch(resetCalculatedPrices());

    const debounceId = window.setTimeout(() => {
      // Флаг читаем в момент отправки: если эффект перезапустился раньше,
      // проверка не теряется, а уходит со следующим запросом.
      const validatePromo = validatePromoNext.current && !!promoCode;
      validatePromoNext.current = false;

      calculateCart(
        buildCalculateCartBody(promoCode, redeemedPoints),
        validatePromo ? { validatePromo: true } : undefined,
      );
    }, 300);

    return () => window.clearTimeout(debounceId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    promoCode,
    availableBasketItems,
    calculateCart,
    deliveryPrice,
    branchId,
    isRedeemingPoints,
    promoAttempt,
    totalPrice,
    user._id,
    user.phone,
    userAddress,
  ]);

  // Пользователь правит код руками — применённый снимается: в расчёте не
  // должен оставаться код, которого уже нет в поле. Новый применится по
  // «Применить».
  const handlePromoInputChange = (value: string) => {
    setPromoInput(value);
    if (promoCode && value.trim() !== promoCode) dispatch(resetPromo());
  };

  // Пересчёт без кода запустит эффект — promoCode в сторе меняется. Кода
  // нет ни в расчёте, ни в статусе — отменять нечего: только чистим поле.
  const handleCancelPromo = () => {
    if (!promoCode && promoValidationStatus === "none") {
      setPromoInput("");
      return;
    }

    impactOccuer("light");
    validatePromoNext.current = false;
    setPromoInput("");
    dispatch(resetPromo());
  };

  const handleApplyPromo = () => {
    const code = promoInput.trim();

    if (promoValidationStatus === "valid" && code === activePromoCode) {
      handleCancelPromo();
      return;
    }

    // Промокод и баллы вместе не применяются, и сами мы ничего не
    // переключаем: пока списание включено, «Применить» заблокировано (см.
    // promo.isBlockedByPoints) — пользователь сначала выключает баллы.
    if (pointsEnabled) return;

    // Пустое поле — как и с «Отменой», ничего не делаем: ни запроса, ни
    // красной подсветки «неверный код» для кода, которого не вводили.
    if (!code) return;

    impactOccuer("light");
    // Новый код заменяет прежний; проверка уходит одним пересчётом из
    // эффекта.
    validatePromoNext.current = true;
    dispatch(applyPromoCode(code));
    setPromoAttempt((attempt) => attempt + 1);
  };

  // Тумблер «Списать баллы». Сумму не выбираем — стор подставит весь
  // discountAmountForCurrentOrder, а эффект пересчёта отправит его в
  // calculate-cart.
  const togglePoints = (enabled: boolean) => {
    impactOccuer("light");
    dispatch(setPointsEnabled(enabled));
  };

  // Самовывоз забирают в ресторане — доставки в нём нет.
  const effectiveDeliveryPrice = isPickupMode ? 0 : deliveryPrice || 0;
  const frontendLastPrice = Math.max(
    0,
    totalPrice + effectiveDeliveryPrice - redeemedPoints,
  );
  const lastPrice = isPickupMode
    ? frontendLastPrice
    : Math.max(0, calculatedTotalPrice ?? frontendLastPrice);

  const summaryDeliveryPrice = calculatedDeliveryPrice ?? deliveryPrice ?? 0;
  const summarySubtotalPrice =
    calculatedSubtotalPrice !== null && calculatedSubtotalPrice !== 0
      ? calculatedSubtotalPrice
      : totalPrice;

  const promotionDiscount = appliedPromotions.reduce(
    (sum, promotion) => sum + promotion.amount,
    0,
  );
  // Скидку бэкенда показываем только по завершённому расчёту и только если
  // она осмысленна — не больше самой корзины.
  const backendTotalDiscount =
    !isCalculatingCart &&
    calculatedTotalPrice !== null &&
    calculatedSubtotalPrice !== null &&
    calculatedDeliveryPrice !== null &&
    calculatedTotalPrice < frontendLastPrice &&
    frontendLastPrice - calculatedTotalPrice <= totalPrice
      ? frontendLastPrice - calculatedTotalPrice
      : 0;

  // Статус показываем, только пока в поле тот самый код, который проверяли.
  const isPromoInvalid =
    promoValidationStatus === "invalid" &&
    Boolean(promoCode) &&
    promoInput.trim() === promoCode;
  const isPromoValid =
    promoValidationStatus === "valid" && promoInput.trim() === activePromoCode;

  return {
    isAuth: user.isAuth,
    isCalculatingCart,
    promo: {
      value: promoInput,
      isValid: isPromoValid,
      isInvalid: isPromoInvalid,
      onChange: handlePromoInputChange,
      onApply: handleApplyPromo,
      /** Включено списание баллов — промокод применить нельзя. */
      isBlockedByPoints: pointsEnabled,
      onCancel: handleCancelPromo,
    },
    points: {
      /** Сколько можно списать в этом заказе — число на зелёной плашке. */
      available: availableToRedeem,
      enabled: pointsEnabled,
      /** Лимит в % от чека для подсказки; null — бэкенд не прислал. */
      limitPercent: bonusPaymentPercent,
      redeemed: redeemedPoints,
      // С промокодом (применённым или ещё на проверке) баллы не списываются.
      isDisabled: hasPromoInCalculation || !availableToRedeem,
      /** Тумблер закрыт именно промокодом — для подсказки под ним. */
      isBlockedByPromo: hasPromoInCalculation,
      onToggle: togglePoints,
    },
    summary: {
      deliveryPrice: summaryDeliveryPrice,
      subtotal: summarySubtotalPrice,
      promotions: appliedPromotions,
      promotionDiscount,
      backendTotalDiscount,
      earnedAmount,
      lastPrice,
    },
  };
};

export type OrderCalculation = ReturnType<typeof useOrderCalculation>;
